import React, { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { fetchDashboardOverview } from '../adminApi';

function KpiCard({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4">
      <div className="text-xs font-medium text-zinc-500 uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-white tabular-nums">
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const o = await fetchDashboardOverview();
        if (!cancelled) setData(o);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="text-zinc-400 text-sm">Loading dashboard…</div>
    );
  }

  if (error) {
    return <div className="text-red-400 text-sm">{error}</div>;
  }

  const topChartData = (data.topViewed || []).map((m) => ({
    title: m.title?.length > 28 ? `${m.title.slice(0, 28)}…` : m.title,
    views: Number(m.view_count) || 0
  }));

  const signupData = data.signupSeries || [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-white">Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Overview from your Supabase data. &quot;Recently added&quot; uses{' '}
          <code className="text-zinc-400">published_date</code>. Daily video
          views need a future analytics table.
        </p>
      </div>

      {data.errorSummary && (
        <div className="rounded-lg border border-amber-900/50 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
          <span className="font-medium">Some queries failed: </span>
          {data.errorSummary}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Movies" value={data.movieCount?.toLocaleString() ?? '0'} />
        <KpiCard
          label="Collections"
          value={data.categoryCount?.toLocaleString() ?? '0'}
          hint="Curated groups (many-to-many with movies)"
        />
        <KpiCard
          label="Subscribers (active)"
          value={data.subscriberActive?.toLocaleString() ?? '0'}
        />
        <KpiCard
          label="Subscribers (inactive)"
          value={data.subscriberInactive?.toLocaleString() ?? '0'}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
          <h2 className="text-sm font-medium text-zinc-300 mb-4">
            New signups (30 days)
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={signupData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#a1a1aa', fontSize: 10 }}
                  tickFormatter={(v) => v.slice(5)}
                />
                <YAxis tick={{ fill: '#a1a1aa', fontSize: 10 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: '#18181b',
                    border: '1px solid #3f3f46',
                    borderRadius: 8
                  }}
                  labelStyle={{ color: '#e4e4e7' }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
          <h2 className="text-sm font-medium text-zinc-300 mb-4">
            Most watched (by view count)
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topChartData} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                <XAxis type="number" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
                <YAxis
                  type="category"
                  dataKey="title"
                  width={120}
                  tick={{ fill: '#a1a1aa', fontSize: 9 }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#18181b',
                    border: '1px solid #3f3f46',
                    borderRadius: 8
                  }}
                />
                <Bar dataKey="views" fill="#a78bfa" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h2 className="text-sm font-medium text-zinc-300">Recently added</h2>
        </div>
        <ul className="divide-y divide-zinc-800">
          {(data.recentMovies || []).map((m) => (
            <li
              key={m.video_id}
              className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
            >
              <span className="text-sm text-white font-medium">{m.title}</span>
              <span className="text-xs text-zinc-500">
                {m.channel_name}
                {m.published_date
                  ? ` · ${new Date(m.published_date).toLocaleDateString()}`
                  : ''}
              </span>
            </li>
          ))}
          {(!data.recentMovies || data.recentMovies.length === 0) && (
            <li className="px-4 py-6 text-sm text-zinc-500 text-center">
              No movies found.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
